'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  ConfirmationDialog,
  IconButton,
  ListNavigation,
  Tooltip,
} from '@terra/ui-design-system';
import {
  Download01Icon,
  Message01Icon,
  Edit03Icon,
  SentIcon,
} from '@terra/ui-icons';
import { useFindingPublishConfirm } from '../../hooks/useFindingPublishConfirm';
import { ArchivedActionGate } from '../common/ArchivedActionGate';

type FindingDetailsHeaderActionsProps = {
  findingId: string;
  isPublished: boolean;
  isExporting?: boolean;
  isPublishing?: boolean;
  isArchived?: boolean;
  findingIds: string[];
  entityTitle: string;
  onSendToChat: () => void;
  onEdit: () => void;
  onExport: () => void;
  onPublish: () => void | Promise<void>;
  onNavigate: (findingId: string) => void;
};

export function FindingDetailsHeaderActions({
  findingId,
  isPublished,
  isExporting,
  isPublishing,
  isArchived = false,
  findingIds,
  entityTitle,
  onSendToChat,
  onEdit,
  onExport,
  onPublish,
  onNavigate,
}: FindingDetailsHeaderActionsProps) {
  const tFindingsRow = useTranslations('findingsTab.row');
  const tFindingDetail = useTranslations('designSystem.findings.findingDrawer');
  const tEditDrawer = useTranslations('designSystem.findings.editDrawer');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const archivedActionTooltip = tLifecycle('archivedFindingActionTooltip');
  const {
    isPublishConfirmOpen,
    setIsPublishConfirmOpen,
    handlePublishConfirmOpenChange,
    handleConfirmPublish,
  } = useFindingPublishConfirm({ onPublish });
  const currentIndex = findingIds.indexOf(findingId);
  const showListNavigation = currentIndex >= 0 && findingIds.length > 0;
  const listCurrentIndex = currentIndex + 1;
  const publishLabel = tFindingsRow('publish');
  const sendToChatLabel = tFindingDetail('sendToChatButton');
  const editLabel = tEditDrawer('edit');
  const exportLabel = tFindingDetail('exportButton');
  const navigateByOffset = useCallback(
    (offset: -1 | 1) => {
      const id = findingIds[currentIndex + offset];

      if (id) {
        onNavigate(id);
      }
    },
    [currentIndex, findingIds, onNavigate],
  );
  const goToPreviousFinding = useCallback(
    () => navigateByOffset(-1),
    [navigateByOffset],
  );
  const goToNextFinding = useCallback(
    () => navigateByOffset(1),
    [navigateByOffset],
  );

  return (
    <>
      {showListNavigation ? (
        <HStack gap="2" alignItems="center" ml={2} flexShrink={0}>
          <ListNavigation
            currentIndex={listCurrentIndex}
            total={findingIds.length}
            onPrevious={goToPreviousFinding}
            onNext={goToNextFinding}
            entityTitle={entityTitle}
          />
          <Box
            role="separator"
            aria-orientation="vertical"
            borderLeftWidth="1px"
            borderColor="border.subtle"
            h={4}
            flexShrink={0}
          />
        </HStack>
      ) : null}
      <HStack gap="2">
        {isPublished ? null : (
          <ArchivedActionGate
            isArchived={isArchived}
            tooltip={archivedActionTooltip}
          >
            <Tooltip title={publishLabel}>
              <IconButton
                variant="ghost"
                size="xs"
                colorPalette="green"
                aria-label={publishLabel}
                onClick={() => setIsPublishConfirmOpen(true)}
                disabled={isPublishing}
                loading={isPublishing}
              >
                <SentIcon size={14} />
              </IconButton>
            </Tooltip>
          </ArchivedActionGate>
        )}
        <ArchivedActionGate
          isArchived={isArchived}
          tooltip={archivedActionTooltip}
        >
          <Tooltip title={sendToChatLabel}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={sendToChatLabel}
              onClick={onSendToChat}
            >
              <Message01Icon size={14} />
            </IconButton>
          </Tooltip>
        </ArchivedActionGate>
        <ArchivedActionGate
          isArchived={isArchived}
          tooltip={archivedActionTooltip}
        >
          <Tooltip title={editLabel}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={editLabel}
              onClick={onEdit}
            >
              <Edit03Icon size={14} />
            </IconButton>
          </Tooltip>
        </ArchivedActionGate>
        {isPublished ? (
          <Tooltip title={exportLabel}>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={exportLabel}
              onClick={onExport}
              loading={isExporting}
            >
              <Download01Icon size={14} />
            </IconButton>
          </Tooltip>
        ) : null}
      </HStack>
      <ConfirmationDialog
        isOpen={isPublishConfirmOpen}
        onOpenChange={handlePublishConfirmOpenChange}
        title={tEditDrawer('publishConfirmation.title')}
        description={tEditDrawer('publishConfirmation.message')}
        confirmText={tEditDrawer('publishConfirmation.yes')}
        cancelText={tEditDrawer('publishConfirmation.no')}
        onConfirm={handleConfirmPublish}
        loading={isPublishing}
        variant="warning"
      />
    </>
  );
}
